import { Container, useBreakpointValue } from '@chakra-ui/react'
import { captureException } from '@sentry/nextjs'
import { GetServerSideProps } from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import Head from 'next/head'
import { useState } from 'react'

import { getContributors, getSubscription } from '@miimosa/api/v2/routes'
import { ProjectsAPI, extractUserID } from '@miimosa/api/v3'
import {
  v3toFrontSearchProject,
  type FrontProjectType,
  type FrontSearchProjectType,
  type Subscription,
  type Lang,
  extractString,
  v3toFrontProject,
  type contributorsType,
} from '@miimosa/common'
import { parseLocale } from '@miimosa/design-system/lib/hooks/use_platform'
import type { Projects, Search } from '@miimosa/protocol'

import { DonationProjectInterface, LendingProjectInterface, NewProjectList } from '@components'
import { Steps } from '@components/pages/projects'

const GetAsyncProjects = async (params: Search.FeaturedProjectsRequest): Promise<FrontSearchProjectType[]> => {
  const projectsApi = new ProjectsAPI()
  try {
    const results = await projectsApi.getFeaturedProjects(params)
    if (results.kind === 'success') {
      const formattedProjects = results.data.projects.map((project) =>
        v3toFrontSearchProject({ ...project, imageFileSize: 'card' })
      )
      return formattedProjects
    }
    return []
  } catch {
    return []
  }
}

async function fetchProjects(slug: string, locale: Lang, userId?: number): Promise<Projects.FetchProjectResponse> {
  const projectsAPIv3 = new ProjectsAPI()

  try {
    const results = await projectsAPIv3.fetchProject({
      slug: slug,
      user_id: userId,
      locale,
    })

    return (results.kind === 'success' ? results.data : {}) as Projects.FetchProjectResponse
  } catch (err) {
    captureException(err)
    return {} as Projects.FetchProjectResponse
  }
}

const retriveSubscription = async (slug: string, userId: number) => {
  try {
    const { data } = await getSubscription(slug, {
      user_id: userId,
    })
    return data
  } catch (err) {
    captureException(err)
  }
}

const retriveContributors = async (slug: string) => {
  try {
    const { data } = await getContributors(slug)
    return data
  } catch (err) {
    captureException(err)
  }
}

export const getServerSideProps: GetServerSideProps = async ({ locale, params, req }) => {
  const { lang, platform } = parseLocale(locale)
  const slugParams = params?.slug
  const slug = extractString(slugParams)
  const userId = await extractUserID(req)

  let projectV3 = undefined

  if (slug) projectV3 = await fetchProjects(slug, lang, userId)
  const projects = await GetAsyncProjects({ branch_code: `mii_${platform}`, locale: lang })

  if (!projectV3) {
    return {
      notFound: true,
    }
  }

  let subscriptionState: Subscription | undefined
  if (slug && userId) subscriptionState = await retriveSubscription(slug, userId)

  let contributors: contributorsType | undefined
  if (slug) contributors = await retriveContributors(slug)

  const formattedProject = v3toFrontProject(projectV3)

  return {
    props: {
      ...(await serverSideTranslations(locale || 'fr', ['common', 'projects', 'home', 'merchant', 'project'])),
      projects,
      project: formattedProject,
      subscriptionState: subscriptionState?.subscription || null,
      contributors: contributors || { contributors: [], hidden_contributors: 0 },
    },
  }
}

interface Props {
  project: FrontProjectType
  projects: FrontSearchProjectType[]
  contributors: contributorsType
  subscriptionState: Subscription['subscription']
}

const Contributors = ({ project, projects, subscriptionState, contributors }: Props) => {
  const [activeTab] = useState('contributors')
  const itemsPerPage = useBreakpointValue({ base: 1, md: 2, lg: 3, xl: 4, '2xl': 5 }, { fallback: 'xl' }) as number

  return (
    <>
      <Head>
        <title>{project.title}</title>
        <meta property="og:title" content={project.title} key="title" />
        <meta name="description" content={project.description?.shortDescription} />
        <meta property="og:description" content={project.description?.shortDescription} key="description" />
        <meta property="og:image" content={project.description?.imageFileName} key="image" />
      </Head>
      {project.collect.type == 'donation' ? (
        <DonationProjectInterface
          project={project}
          activeTab={activeTab}
          subscriptionState={subscriptionState}
          contributors={contributors}
        />
      ) : (
        <LendingProjectInterface
          project={project}
          activeTab={activeTab}
          subscriptionState={subscriptionState}
          contributors={contributors}
        />
      )}
      <Container variant="full" p={0} bgColor="creamy">
        <Steps collectType={project.collect.type} />
        <NewProjectList projects={projects} itemsPerPage={itemsPerPage} />
      </Container>
    </>
  )
}

export default Contributors
